<script setup lang="ts">
//#region imports
import { computed } from 'vue';
import LanguagesSelect from '@/components/UIElements/LanguagesSelect.vue';
//#endregion

//#region types
interface Props {
  selectedIndex?: number,
  languages?: any,
  listLanguages?: any,
  selectedLanguages?: any,
  errors?: any,
  canEdit?: boolean,
  canMakeDefault?: boolean,
}

interface  Emits {
  'addLanguage': [languageCode: string],
  'selectIndex': [index: number],
  'deleteLanguage': [],
  'makeDefault': [],
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  selectedIndex: -1,
  languages: [],
  listLanguages: null,
  selectedLanguages: [],
  errors: [],
  canEdit: true,
  canMakeDefault: true,
});

const emit = defineEmits<Emits>();
//#endregion

const currentLangName = computed(() => {
  return props.languages[props.selectedIndex]?.name;
});
const currentLangDefault = computed(() => {
  return props.languages[props.selectedIndex]?.is_default;
});

//#region methods
const handleAddLanguage = (languageCode: string) => {
  emit('addLanguage', languageCode);
};
//#endregion
</script>

<template>
  <div class="languages-setup">
    <div class="languages-setup__list t:space-y-16">
      <UiLabel
        :label="$t(`marketingMaterials.popup.form.language_codes.label`)"
        :font-size="16"
        bold
      />
      <LanguagesSelect
        v-if="props.canEdit"
        :languages="props.listLanguages"
        :selected="props.selectedLanguages"
        @select="handleAddLanguage"
      />
      <div>
        <div
          v-for="(language, languageIndex) in languages"
          :key="language.language_code"
          class="languages-setup__item"
          :class="{
            'languages-setup__item_active': selectedIndex === languageIndex,
            'languages-setup__item_error': !!errors[languageIndex],
          }"
          @click="$emit('selectIndex', Number(languageIndex))"
        >
          <div class="languages-setup__item-flag">
            <Flag
              v-if="language.countryCode"
              :iso="language.countryCode"
            />
            <UiIcon
              v-else
              name="globe"
            />
          </div>
          <div>
            <div class="t:flex t:items-center t:gap-4">
              <UiLabel
                :label="language.name"
                :font-size="12"
                bold
              />
              <UiIcon
                v-if="language.is_default"
                :size="10"
                color="var(--text-link)"
                name="home"
              />
            </div>
            <div
              v-if="props.canEdit"
              :class="[
                'languages-setup__item-status',
                `languages-setup__item-status_${!language.message || language.title === '' ? 'negative' : 'positive'}`,
              ]"
            >
              {{ !language.message || language.title === '' ? $t('crm.activities.actions.empty') : $t('crm.activities.actions.completed') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="languages-setup__content t:space-y-16">
      <div class="t:flex t:items-center t:justify-between">
        <UiLabel
          :label="currentLangName"
          :font-size="16"
          bold
        />
        <div
          v-if="props.canEdit"
          class="t:flex t:gap-16"
        >
          <UiButton
            v-if="props.canMakeDefault"
            icon="home"
            no-border
            :disabled="currentLangDefault"
            @click="$emit('makeDefault')"
          >
            {{ $t('crm.buttons.make_default') }}
          </UiButton>
          <UiButton
            icon="trash-alt"
            type="negative"
            no-border
            :disabled="currentLangDefault "
            @click="$emit('deleteLanguage')"
          >
            {{ $t('crm.buttons.delete') }}
          </UiButton>
        </div>
      </div>
      <slot />
    </div>
  </div>
</template>

<style lang="scss">
.languages-setup {
  border-top: 1px solid var(--border-default);
  display: flex;
  padding: 0 24px;
  &__list {
    padding: 16px 16px 16px 0;
    border-right: 1px solid var(--border-default);
  }
  &__item {
    display: flex;
    padding: 8px;
    gap: 8px;
    border-radius: 5px;
    align-items: center;
    cursor: pointer;
    transition: all 0.3s;
    min-width: 180px;
    &_error {
      background: var(--background-negative-subtle);
    }
    &_active {
      box-shadow: 0 0 0 1px var(--border-focused);
      background: var(--background-brand-subtle);
    }
    &:hover {
      background: var(--background-brand-subtle);
    }
  }
  &__item-flag {
    display: flex;
    width: 24px;
    min-width: 24px;
    height: 24px;
    justify-content: center;
    align-items: center;

    border-radius: 100px;
    border: 1px solid var(--border-default);
    background: var(--background-surface);
  }
  &__content {
    padding: 16px 0 16px 16px;
    flex-grow: 1;
  }

  &__item-status {
    font-size: 10px;
    font-weight: 500;
    &_positive {
      color: var(--text-positive);
    }
    &_negative {
      color: var(--text-negative);
    }
  }
}
</style>
